import localDatabase from './localDatabase';
import { customerService, reservationService, shiftService, tableGroupService, tableService } from './supabaseService';
import { publishCrossTab } from './crossTab';
import { supabase } from '../lib/supabase';
import { loadActiveTenant } from '../lib/tenantStorage';
import * as Sentry from '@sentry/react';

const DEBUG_SYNC_LOGS =
  (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_DEBUG_SYNC_LOGS === 'true') ||
  (typeof process !== 'undefined' && (process as any).env?.VITE_DEBUG_SYNC_LOGS === 'true');

const syncLogger = {
  log: (...args: any[]) => {
    if (DEBUG_SYNC_LOGS) {
      console.log(...args);
    }
  },
  warn: (...args: any[]) => {
    if (DEBUG_SYNC_LOGS) {
      console.warn(...args);
    }
  },
  error: (...args: any[]) => {
    if (DEBUG_SYNC_LOGS) {
      console.error(...args);
    }
  },
};

// Track if Supabase is reachable to avoid repeated failed requests
let supabaseReachable = true;
let lastReachabilityCheck = 0;
const REACHABILITY_CHECK_INTERVAL = 30_000; // 30 seconds
const NO_SHOW_OVERRIDE_STORAGE_KEY = 'reservations:no-show-overrides';

function readNoShowOverrideMap(): Record<string, true> {
  if (typeof window === 'undefined') return {};
  const raw = window.localStorage.getItem(NO_SHOW_OVERRIDE_STORAGE_KEY);
  if (!raw) return {};

  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const normalized: Record<string, true> = {};
    for (const id of Object.keys(parsed as Record<string, unknown>)) {
      normalized[id] = true;
    }
    return normalized;
  } catch {
    return {};
  }
}

async function checkSupabaseReachable(): Promise<boolean> {
  const now = Date.now();
  // Skip cache check if we're doing a fresh check (lastReachabilityCheck = 0)
  if (lastReachabilityCheck > 0 && now - lastReachabilityCheck < REACHABILITY_CHECK_INTERVAL && !supabaseReachable) {
    syncLogger.log('[supabaseSync] Skipping reachability check - recently failed');
    return false;
  }
  lastReachabilityCheck = now;

  try {
    // Quick health check - just try to list shifts (lightweight)
    syncLogger.log('[supabaseSync] Checking Supabase reachability...');
    const tenantId = await getActiveTenantId();
    if (!tenantId) {
      syncLogger.warn('[supabaseSync] No active tenant; skipping reachability check');
      return false;
    }
    const shifts = await shiftService.listAllShifts(tenantId);
    supabaseReachable = true;
    syncLogger.log(`[supabaseSync] Supabase is reachable! Found ${shifts.length} shifts`);
    return true;
  } catch (error: any) {
    supabaseReachable = false;
    syncLogger.warn('[supabaseSync] Supabase not reachable:', error?.message || error);
    return false;
  }
}

function isOnline() {
  return typeof navigator !== 'undefined' && navigator.onLine;
}

async function getActiveTenantId(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  const userId = data.session?.user?.id;
  if (!userId) return null;
  return loadActiveTenant(userId);
}

export async function pushPendingToSupabase() {
  if (!isOnline()) return false;

  const tenantId = await getActiveTenantId();
  if (!tenantId) {
    syncLogger.warn('[supabaseSync] No active tenant selected; skipping push.');
    return false;
  }

  try {
    const pending = await localDatabase.getPendingSyncItems();
    if (!pending.length) return true;

    // Process with deterministic priority to satisfy FK ordering
    const priority: Record<string, number> = {
      customers: 0,
      tables: 1,
      table_groups: 2,
      shifts: 3,
      reservations: 4,
    };
    pending.sort((a, b) => (priority[a.table_name] ?? 99) - (priority[b.table_name] ?? 99));

    // Check if Supabase is reachable before attempting to push
    const reachable = await checkSupabaseReachable();
    if (!reachable) {
      syncLogger.log('[supabaseSync] Skipping push - Supabase not reachable');
      Sentry.withScope((scope) => {
        scope.setTag('operation', 'sync_push');
        scope.setContext('sync', { pendingItems: pending.length });
        Sentry.captureMessage('Supabase sync: unreachable', 'warning');
      });
      return false;
    }

    let successCount = 0;
    let failCount = 0;

    for (const item of pending) {
      try {
        const payload = { ...(item.data || {}), tenant_id: (item.data?.tenant_id ?? tenantId) };
        switch (item.table_name) {
          case 'customers':
            if (item.operation === 'DELETE') {
              await customerService.deleteCustomerRecord(item.record_id, tenantId);
            } else {
              // Resolve duplicate-by-phone collisions before upserting to Supabase
              const phone = payload?.phone?.trim?.();
              if (phone && payload?.id) {
                try {
                  const existingRemote = await customerService.getCustomerByPhone(phone, tenantId);
                  if (existingRemote && existingRemote.id !== payload.id) {
                    // Align local data/queue to the remote customer id to avoid FK issues
                    await localDatabase.remapCustomerId(payload.id, existingRemote.id, existingRemote as any);
                    await localDatabase.markSyncItemProcessed(item.id, true);
                    successCount++;
                    continue;
                  }
                } catch (err) {
                  syncLogger.warn('[supabaseSync] Remote customer lookup failed', err);
                }
              }

              await customerService.upsertCustomerRecord(payload);
            }
            break;
          case 'tables':
            if (item.operation === 'DELETE') {
              await tableService.deleteTableRecord(item.record_id, tenantId);
            } else {
              const number = typeof payload?.number === 'number' ? payload.number : parseInt(payload?.number ?? '', 10);
              if (!number || !payload?.seats || payload.seats <= 0) {
                syncLogger.warn('[supabaseSync] Skipping invalid table payload', payload);
                await localDatabase.markSyncItemProcessed(item.id, false);
                failCount++;
                continue;
              }

              const validStatuses = ['active', 'inactive', 'maintenance'];
              const status = (payload?.status || '').toString().trim().toLowerCase();
              if (!validStatuses.includes(status)) {
                // Map legacy/invalid statuses to a valid one
                payload.status = 'active';
              }
              if (!payload.manual_status) {
                payload.manual_status = 'available';
              }
              if (!payload.updated_at) {
                payload.updated_at = new Date().toISOString();
              }

              // Resolve duplicate-by-number collisions before upserting
              try {
                const existingRemote = await tableService.getTableByNumberForTenant(number, tenantId);
                if (existingRemote && existingRemote.id !== payload.id) {
                  await localDatabase.remapTableId(payload.id, existingRemote.id, existingRemote as any);
                  await localDatabase.markSyncItemProcessed(item.id, true);
                  successCount++;
                  continue;
                }
              } catch (err) {
                syncLogger.warn('[supabaseSync] Remote table lookup failed', err);
              }

              await tableService.upsertTableRecord(payload);
            }
            break;
          case 'shifts':
            if (item.operation === 'DELETE') {
              await shiftService.deleteShiftRecord(item.record_id, tenantId);
            } else {
              await shiftService.upsertShiftRecord(payload);
            }
            break;
          case 'table_groups':
            if (item.operation === 'DELETE') {
              await tableGroupService.deleteTableGroupRecord(item.record_id, tenantId);
            } else {
              await tableGroupService.upsertTableGroupRecord(payload);
            }
            break;
          case 'reservations':
            if (item.operation === 'DELETE') {
              await reservationService.deleteReservationRecord(item.record_id, tenantId);
            } else if (item.operation === 'INSERT') {
              // Keep caller-provided id so local and remote stay aligned.
              // Strip fields not in the Supabase schema (e.g. `source` used locally).
              const { created_at: _c, updated_at: _u, source: _src, ...rest } = payload as any;

              // Normalize status to match Supabase constraint (includes in-progress)
              const allowedStatuses = ['confirmed', 'in-progress', 'cancelled', 'completed', 'no-show'];
              const rawStatus = (rest as any).status;
              if (!allowedStatuses.includes(rawStatus)) {
                (rest as any).status = 'confirmed';
              }

              // Ensure shift exists remotely (FK guard)
              const shiftId = (rest as any).shift_id;
              if (shiftId) {
                try {
                  const remoteShift = await shiftService.getShift(shiftId, tenantId);
                  if (!remoteShift) {
                    throw new Error('Shift missing remotely');
                  }
                } catch (_err) {
                  const localShift = await localDatabase.getShift(shiftId);
                  if (localShift) {
                    await shiftService.upsertShiftRecord({ ...localShift, tenant_id: tenantId } as any);
                    syncLogger.log('[supabaseSync] Pushed missing shift before reservation', shiftId);
                  } else {
                    syncLogger.warn('[supabaseSync] Cannot push reservation - shift not found locally', shiftId);
                    await localDatabase.markSyncItemProcessed(item.id, false);
                    failCount++;
                    continue;
                  }
                }
              }

              // Ensure table exists remotely if provided (FK guard)
              const tableId = (rest as any).table_id;
              if (tableId) {
                try {
                  const remoteTable = await tableService.getTable(tableId, tenantId);
                  if (!remoteTable) {
                    throw new Error('Table missing remotely');
                  }
                } catch (_err) {
                  const localTable = await localDatabase.getTable(tableId);
                  if (localTable) {
                    await tableService.upsertTableRecord({ ...localTable, tenant_id: tenantId } as any);
                    syncLogger.log('[supabaseSync] Pushed missing table before reservation', tableId);
                  } else {
                    syncLogger.warn('[supabaseSync] Cannot push reservation - table not found locally', tableId);
                    await localDatabase.markSyncItemProcessed(item.id, false);
                    failCount++;
                    continue;
                  }
                }
              }

              // FK Check: Ensure customer exists in Supabase before inserting reservation
              const customerId = (rest as any).customer_id;
              if (customerId) {
                try {
                  const customer = await customerService.getCustomer(customerId, tenantId);
                  if (!customer) {
                    // Customer doesn't exist remotely - try to push it first
                    const localCustomer = await localDatabase.getCustomer(customerId);
                    if (localCustomer) {
                      await customerService.upsertCustomerRecord({ ...localCustomer, tenant_id: tenantId } as any);
                      syncLogger.log('[supabaseSync] Pushed missing customer before reservation', customerId);
                    } else {
                      // Customer doesn't exist locally either - skip this reservation
                      syncLogger.warn('[supabaseSync] Cannot push reservation - customer not found', customerId);
                      await localDatabase.markSyncItemProcessed(item.id, false);
                      failCount++;
                      continue;
                    }
                  }
                } catch (err) {
                  syncLogger.warn('[supabaseSync] Customer FK check failed, attempting push anyway', err);
                }
              }

              // Use simple insert WITHOUT auto-assignment logic
              // This respects whatever table_id the user chose (or null if no table)
              await reservationService.insertReservationRecord({ ...rest, tenant_id: tenantId });
            } else if (item.operation === 'UPDATE') {
              // Strip fields not in the Supabase schema before updating
              if (payload && typeof payload === 'object') {
                delete (payload as any).source;
              }
              // Use conflict-safe update path (runs server conflict checks when table/time change)
              const allowedStatuses = ['confirmed', 'in-progress', 'cancelled', 'completed', 'no-show'];
              const rawStatus = payload?.status;
              if (rawStatus && !allowedStatuses.includes(rawStatus)) {
                payload.status = 'confirmed';
              }

              // Ensure shift exists remotely (FK guard)
              const shiftId = payload?.shift_id;
              if (shiftId) {
                try {
                  const remoteShift = await shiftService.getShift(shiftId, tenantId);
                  if (!remoteShift) {
                    throw new Error('Shift missing remotely');
                  }
                } catch (_err) {
                  const localShift = await localDatabase.getShift(shiftId);
                  if (localShift) {
                    await shiftService.upsertShiftRecord({ ...localShift, tenant_id: tenantId } as any);
                    syncLogger.log('[supabaseSync] Pushed missing shift before reservation update', shiftId);
                  } else {
                    syncLogger.warn('[supabaseSync] Cannot update reservation - shift not found locally', shiftId);
                    await localDatabase.markSyncItemProcessed(item.id, false);
                    failCount++;
                    continue;
                  }
                }
              }

              // Ensure table exists remotely if provided (FK guard)
              const tableId = payload?.table_id;
              if (tableId) {
                try {
                  const remoteTable = await tableService.getTable(tableId, tenantId);
                  if (!remoteTable) {
                    throw new Error('Table missing remotely');
                  }
                } catch (_err) {
                  const localTable = await localDatabase.getTable(tableId);
                  if (localTable) {
                    await tableService.upsertTableRecord({ ...localTable, tenant_id: tenantId } as any);
                    syncLogger.log('[supabaseSync] Pushed missing table before reservation update', tableId);
                  } else {
                    syncLogger.warn('[supabaseSync] Cannot update reservation - table not found locally', tableId);
                    await localDatabase.markSyncItemProcessed(item.id, false);
                    failCount++;
                    continue;
                  }
                }
              }

              await reservationService.updateReservation(item.record_id, { ...payload, tenant_id: tenantId });
            }
            break;
          default:
            console.warn('[supabaseSync] Unknown table in sync queue', item.table_name);
            break;
        }
        await localDatabase.markSyncItemProcessed(item.id, true);
        successCount++;
      } catch (error: any) {
        failCount++;
        // If we get a network error, mark Supabase as unreachable and stop trying
        if (error?.message?.includes('Failed to fetch') || error?.code === 'ERR_NAME_NOT_RESOLVED') {
          supabaseReachable = false;
          Sentry.captureException(error, {
            tags: { operation: 'sync_push_network_error', severity: 'high' },
            contexts: { sync: { table: item.table_name, operation: item.operation } }
          });
          syncLogger.warn('[supabaseSync] Network error, marking Supabase unreachable');
          break;
        }
        Sentry.captureException(error, {
          tags: { operation: 'sync_push_item_failed', severity: 'medium' },
          contexts: { sync: { table: item.table_name, operation: item.operation, recordId: item.record_id } }
        });
        syncLogger.warn('[supabaseSync] Failed to push sync item', item.table_name, item.operation, error?.message, error?.details);
        
        // Special handling for UPDATE operations when the remote record is missing.
        if (item.operation === 'UPDATE' && (error?.status === 404 || error?.status === 406 || error?.code === 'PGRST116')) {
          // Check if there's a pending INSERT for the same record
          const pending = await localDatabase.getPendingSyncItems();
          const hasPendingInsert = pending.some(p => 
            p.table_name === item.table_name && 
            p.record_id === item.record_id && 
            p.operation === 'INSERT' &&
            p.id !== item.id
          );
          
          if (hasPendingInsert) {
            // Skip this UPDATE - it will be retried after the INSERT succeeds
            syncLogger.log('[supabaseSync] UPDATE skipped - waiting for INSERT to complete first:', item.record_id);
            // Don't mark as failed or processed - leave it pending for next sync cycle
            // Skip the rest of error handling
          } else if (item.table_name === 'reservations') {
            // No pending INSERT - check if record exists locally
            const localRecord = await localDatabase.getReservation(item.record_id);
            if (localRecord) {
              // Record exists locally but not in Supabase - convert UPDATE to INSERT
              syncLogger.log('[supabaseSync] Converting UPDATE to INSERT for missing record:', item.record_id);
              await localDatabase.markSyncItemProcessed(item.id, false); // Mark UPDATE as failed
              // Create new INSERT sync item
              await localDatabase.addToSyncQueue('reservations', item.record_id, 'INSERT', localRecord);
              // Skip the rest of error handling
            } else {
              // Record doesn't exist locally or remotely - mark as failed
              await localDatabase.markSyncItemProcessed(item.id, false);
              syncLogger.warn('[supabaseSync] UPDATE failed - record not found:', item.record_id);
            }
          } else {
            // Not a reservation or no special handling - mark as failed
            await localDatabase.markSyncItemProcessed(item.id, false);
            syncLogger.warn('[supabaseSync] UPDATE failed with 406:', item.record_id);
          }
        } else {
          // Mark as failed for other client errors (4xx) and constraint violations to stop retrying
          if (error?.status === 400 || 
              error?.status === 406 || // PGRST116 - Cannot coerce to single JSON object
              error?.status === 409 || // Conflict (FK violations, overlaps, etc.)
              error?.code === '23505' || // Unique violation
              error?.code === '23502' || // Not null violation
              error?.code === '23503' || // Foreign key violation
              error?.code === 'PGRST116') { // Cannot coerce result to single JSON object
            // Mark as failed so we don't keep retrying bad payloads
            await localDatabase.markSyncItemProcessed(item.id, false);
            syncLogger.warn('[supabaseSync] Marked sync item as failed (will not retry):', item.id, error?.code || error?.status);
          }
        }
      }
    }

    if (successCount > 0) {
      syncLogger.log(`[supabaseSync] Pushed ${successCount} items to Supabase`);
    }
    if (failCount > 0) {
      Sentry.withScope((scope) => {
        scope.setTag('operation', 'sync_push');
        scope.setContext('sync', { successCount, failCount, totalItems: pending.length });
        Sentry.captureMessage(`Sync push failed for ${failCount} items`, 'warning');
      });
      syncLogger.warn(`[supabaseSync] Failed to push ${failCount} items`);
    }

    return successCount > 0;
  } catch (error) {
    Sentry.captureException(error, {
      tags: { operation: 'sync_push_fatal', severity: 'high' },
      contexts: { sync: { source: 'supabaseSync' } }
    });
    syncLogger.error('[supabaseSync] Failed to process sync queue', error);
    return false;
  }
}

export async function pullLatestFromSupabase(options: { includeReservations?: boolean } = {}) {
  syncLogger.log('[supabaseSync] pullLatestFromSupabase called');
  
  if (!isOnline()) {
    syncLogger.log('[supabaseSync] Offline - skipping pull');
    return false;
  }

  const { includeReservations = true } = options;

  try {
    const tenantId = await getActiveTenantId();
    if (!tenantId) {
      syncLogger.warn('[supabaseSync] No active tenant selected; skipping pull.');
      return false;
    }

    // First try to push any pending changes
    syncLogger.log('[supabaseSync] Pushing pending changes first...');
    await pushPendingToSupabase();

    // Check if Supabase is reachable
    syncLogger.log('[supabaseSync] Checking reachability before pull...');
    const reachable = await checkSupabaseReachable();
    if (!reachable) {
      syncLogger.log('[supabaseSync] Skipping pull - Supabase not reachable');
      return false;
    }

    syncLogger.log('[supabaseSync] Fetching data from Supabase...');
    const [tables, shifts, customers, tableGroups] = await Promise.all([
      tableService.listAllTables(tenantId),
      shiftService.listAllShifts(tenantId),
      customerService.listAllCustomers(tenantId),
      tableGroupService.listAllTableGroups(tenantId),
    ]);

    // If there are pending customer deletions locally, avoid re-introducing them from remote
    const pending = await localDatabase.getPendingSyncItems().catch(() => []);
    const pendingCustomerDeletes = new Set(
      (pending as any[])
        .filter((p) => p.table_name === 'customers' && p.operation === 'DELETE')
        .map((p) => p.record_id)
    );
    const customersFiltered = (customers as any[]).filter((c) => !pendingCustomerDeletes.has(c.id));

    syncLogger.log(
      `[supabaseSync] Received: ${tables.length} tables, ${shifts.length} shifts, ${customers.length} customers, ${tableGroups.length} table groups`,
    );

    // First, prune local records that no longer exist in Supabase (unless they have pending sync items)
    const remoteTableIds = new Set((tables as any[]).map(t => t.id));
    const remoteCustomerIds = new Set(customersFiltered.map(c => c.id));
    const remoteTableGroupIds = new Set((tableGroups as any[]).map((g) => g.id));
    await Promise.all([
      localDatabase.pruneMissingTables(remoteTableIds),
      localDatabase.pruneMissingCustomers(remoteCustomerIds),
      localDatabase.pruneMissingTableGroups(remoteTableGroupIds),
    ]);

    await Promise.all([
      localDatabase.bulkUpsertTables(tables as any),
      localDatabase.bulkUpsertShifts(shifts as any),
      localDatabase.bulkUpsertCustomers(customersFiltered as any),
      localDatabase.bulkUpsertTableGroups(tableGroups as any),
    ]);

    publishCrossTab({ type: 'sync:tables' });
    publishCrossTab({ type: 'sync:shifts' });
    publishCrossTab({ type: 'sync:customers' });
    publishCrossTab({ type: 'sync:table-groups' });

    if (includeReservations) {
      const reservations = await reservationService.listAllReservations(tenantId);
      const noShowOverrides = readNoShowOverrideMap();
      const normalizedReservations = (reservations as any[]).map((reservation) => {
        if (reservation?.status === 'no-show' && noShowOverrides[reservation.id]) {
          return { ...reservation, status: 'confirmed', updated_at: new Date().toISOString() };
        }
        return reservation;
      });
      syncLogger.log(`[supabaseSync] Received: ${normalizedReservations.length} reservations`);

      // First, prune locals that no longer exist remotely (unless pending locally)
      const remoteIds = new Set((normalizedReservations as any[]).map(r => r.id));
      await localDatabase.pruneMissingReservations(remoteIds);

      // Then upsert/insert remote reservations
      await localDatabase.bulkUpsertReservations(normalizedReservations as any);
      publishCrossTab({ type: 'sync:reservations' });
    }

    syncLogger.log('[supabaseSync] Successfully pulled latest data from Supabase');
    return true;
  } catch (error: any) {
    // Mark as unreachable on network errors
    if (error?.message?.includes('Failed to fetch') || error?.code === 'ERR_NAME_NOT_RESOLVED') {
      supabaseReachable = false;
    }
    syncLogger.warn('[supabaseSync] Failed to pull latest data - will use local data', error?.message || error);
    return false;
  }
}

// Reset reachability status (e.g., when user manually triggers sync or on app startup)
export function resetSupabaseReachability() {
  syncLogger.log('[supabaseSync] Resetting reachability status');
  supabaseReachable = true;
  lastReachabilityCheck = 0;
}

// Initialize sync - call on app startup
export async function initializeSync() {
  syncLogger.log('[supabaseSync] Initializing sync service...');
  resetSupabaseReachability();
  
  // Try to pull latest data on startup
  const success = await pullLatestFromSupabase();
  if (success) {
    syncLogger.log('[supabaseSync] Initial sync completed successfully');
  } else {
    syncLogger.log('[supabaseSync] Initial sync failed - will use local data');
  }
  
  return success;
}
